import * as React from 'react'
import { FlatList, View, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Checkbox from '@components/checkbox/Checkbox'
import Button from '@components/button/Button'

import useI18n from '@i18n/useI18n'
import TextInputComponent from '@components/textInput/TextInput'
import sanitize from '@utils/Sanitizer'

interface Props {
  selectedFilter: UserFiltersName
  addFilter: (filter: UserFilter) => void
  filters: UserFilters
  onClose: () => void
  resetSpecificFilter: (filterName: UserFiltersName) => void
  filterItems: FilterItem[]
  initialFilters: UserFilters
}

const ItemSelectionWeb = ({
  selectedFilter,
  addFilter,
  filters,
  onClose,
  resetSpecificFilter,
  filterItems,
  initialFilters,
}: Props) => {
  const i18n = useI18n()
  const [selectedFilters, setSelectedFilters] = React.useState(filters[selectedFilter])
  const [searchText, setSearchText] = React.useState('')

  const data = React.useMemo(
    () => filterItems.filter((fi) => sanitize(fi.name).includes(sanitize(searchText))),
    [searchText]
  )

  const updateSelected = (item: FilterItem) => {
    const prev = selectedFilters.some((e) => e.id === item.id)

    if (prev) {
      setSelectedFilters(selectedFilters.filter((oldItem) => oldItem.id !== item.id))
    } else {
      setSelectedFilters([...selectedFilters, item])
    }
  }

  const validateFilter = () => {
    if (!!selectedFilter) {
      addFilter({ name: selectedFilter, value: selectedFilters })
      onClose()
    }
  }

  const resetFilter = () => {
    resetSpecificFilter(selectedFilter)
    setSelectedFilters(initialFilters[selectedFilter])
  }

  const renderFilterItems = ({ item }: { item: FilterItem }) => {
    const selected = selectedFilters.some((e) => e.id === item.id)

    return (
      <CheckboxContainer key={item.id}>
        <Checkbox label={item.name} onClick={() => updateSelected(item)} value={selected} />
      </CheckboxContainer>
    )
  }

  return (
    <Container>
      <SearchContainer>
        <TextInputComponent
          onChange={setSearchText}
          searchStyle="onDark"
          left={{ name: 'search' }}
          placeholder={i18n.t('screens.incidentList.search')}
          value={searchText}
        />
      </SearchContainer>
      <FilterView>
        <FlatList data={data} keyExtractor={(item) => item.id} renderItem={renderFilterItems} />
      </FilterView>
      <Footer>
        {selectedFilters.length > 0 ? (
          <TouchableOpacity activeOpacity={0.9} onPress={resetFilter}>
            <Text>{i18n.t('screens.incidentList.filters.modalWeb.erase')}</Text>
          </TouchableOpacity>
        ) : (
          <View />
        )}
        <Button label={i18n.t('screens.incidentList.filters.modalWeb.save')} onClick={validateFilter} status="active" />
      </Footer>
    </Container>
  )
}

const Container = styled(View)`
  flex: 1;
`

const SearchContainer = styled(View)`
  padding: 16px 16px 0;
`

const FilterView = styled(View)`
  flex: 1;
  padding: 31px 22px 31px 31px;
`

const CheckboxContainer = styled(View)`
  margin-bottom: 15px;
`

const Footer = styled(View)`
  border-top-width: 1px;
  border-top-color: ${(props) => props.theme.colors.abandoned};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 16px 31px;
`

export default ItemSelectionWeb
